<script lang="ts" setup generic="V">
import type { DateRange, DateValue } from 'reka-ui';
import type { ButtonProps } from '#layers/ui/components/Button.vue';
import type { RangeValueAdapter } from '#layers/ui/utils/range-value-adapter';
import { isSameDay, toCalendarDate } from '@internationalized/date';
import { computed, ref, shallowRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { calendarDateConverter, nowDate, useUserLocale } from '#layers/base/public';
import { formatDateRangeSummary } from '#layers/ui/utils/format-date-range-summary';
import { pickerPopoverContent, pickerPopoverUi } from '#layers/ui/utils/picker-popover';
import DateTimePickerRangePanel from './RangePanel.vue';

interface Props extends Pick<ButtonProps, 'size' | 'disabled'> {
	adapter: RangeValueAdapter<V>;
	readonly?: boolean;
	block?: boolean;
	/** When set, dates after today are unavailable in both panels (e.g. the audit log, which records past events). */
	disableFutureDates?: boolean;
	/** When false, the range bounds are required: no per-bound clear and no clear-range. */
	clearable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	block: true,
	disableFutureDates: false,
	clearable: true,
});

defineSlots<{
	'trigger': (props: {
		rangeFieldDisplay: string;
		isRangeEmpty: boolean;
		canClearRange: boolean;
		clearRange: () => void;
	}) => any;
	'start-extra': () => any;
	'end-extra': () => any;
}>();

const startDate = defineModel<V | null>('startDate');
const endDate = defineModel<V | null>('endDate');

const { t } = useI18n();
const { formatLocale } = useUserLocale();

const todayCalendarDate = computed(() => calendarDateConverter.toCalendar(nowDate())!);

const calendarStartDate = computed(() => props.adapter.toCalendarDate(startDate.value));
const calendarEndDate = computed(() => props.adapter.toCalendarDate(endDate.value));

// The panels display the date part only (a `PlainDate`), regardless of the adapter's value type.
const selectedStartDate = computed(() => calendarDateConverter.toTemporal(calendarStartDate.value));
const selectedEndDate = computed(() => calendarDateConverter.toTemporal(calendarEndDate.value));

const startPlaceholderRef = shallowRef<DateValue>(calendarStartDate.value ?? todayCalendarDate.value);
const endPlaceholderRef = shallowRef<DateValue>(calendarEndDate.value ?? todayCalendarDate.value);

/**
 * Synthetic start boundary when only an end date is set — highlights everything up to the end date.
 * Derived from the earlier of the two placeholders so the span stays small (≤ ~2 months) regardless of era;
 * a fixed epoch would either invert the range for pre-epoch end dates or trigger the y1 browser hang.
 */
const syntheticOpenStart = computed(() => {
	const start = toCalendarDate(startPlaceholderRef.value);
	const end = toCalendarDate(endPlaceholderRef.value);
	const earliest = start.compare(end) < 0 ? start : end;

	return earliest.set({ day: 1 }).subtract({ months: 2 });
});

const displayRange = computed<DateRange>(() => {
	const start = calendarStartDate.value;
	const end = calendarEndDate.value;

	if (start && end) {
		return { start, end };
	}

	// Only a start is set: highlight just that day rather than faking a later end (which read as an
	// auto-filled "start + 1"). The span stays a complete range (end === start) so the calendar
	// doesn't treat it as a selection in progress and echo the end back.
	if (start && !end) {
		return { start, end: start };
	}

	if (!start && end) return { start: syntheticOpenStart.value, end };

	return { start: undefined, end: undefined };
});

/**
 * Reka snaps each panel's placeholder onto the range start on every range change. Since
 * syntheticOpenStart is derived from the placeholders, accepting that snap would create a
 * feedback loop. Reject it when the incoming value already equals the range start; real user
 * navigation carries a different day-of-month and passes through.
 */
const startPlaceholder = computed<DateValue>({
	get: () => startPlaceholderRef.value,
	set: (value: DateValue) => {
		const rangeStart = displayRange.value.start;
		const date = toCalendarDate(value);
		if (!startDate.value && endDate.value && rangeStart && isSameDay(date, rangeStart)) return;
		startPlaceholderRef.value = date;
	},
});

const endPlaceholder = computed<DateValue>({
	get: () => endPlaceholderRef.value,
	set: (value: DateValue) => {
		const rangeStart = displayRange.value.start;
		const date = toCalendarDate(value);
		if (rangeStart && isSameDay(date, rangeStart)) return;
		endPlaceholderRef.value = date;
	},
});

const allowNonContiguousRanges = computed(() => !(startDate.value && !endDate.value));

const isRangeEmpty = computed(() => !startDate.value && !endDate.value);
const canClearRange = computed(() => !props.disabled && !props.readonly && !isRangeEmpty.value && props.clearable);

const isStartSetToNowDisabled = computed(() => {
	const end = calendarEndDate.value;
	return end != null && end.compare(todayCalendarDate.value) < 0;
});

const isEndSetToNowDisabled = computed(() => {
	const start = calendarStartDate.value;
	return start != null && start.compare(todayCalendarDate.value) > 0;
});

const rangeFieldDisplay = computed(() => {
	const start = props.adapter.format(startDate.value, formatLocale.value);
	const end = props.adapter.format(endDate.value, formatLocale.value);

	return formatDateRangeSummary(t, start || undefined, end || undefined, t('date_range_picker.select_date'));
});

function onStartCalendarUpdate(value: DateRange | null) {
	if (!value) return;
	const date = value.start ?? value.end;
	if (!date || isStartDateUnavailable(date)) return;
	const calendarDate = toCalendarDate(date);
	if (calendarStartDate.value && isSameDay(calendarDate, calendarStartDate.value)) return;
	startDate.value = props.adapter.withDate(startDate.value, calendarDate);
}

function onEndCalendarUpdate(value: DateRange | null) {
	if (!value) return;
	const date = value.end ?? value.start;
	if (!date || isEndDateUnavailable(date)) return;
	const calendarDate = toCalendarDate(date);
	if (calendarEndDate.value && isSameDay(calendarDate, calendarEndDate.value)) return;
	endDate.value = props.adapter.withDate(endDate.value, calendarDate);
}

function setStartToNow() {
	if (isStartSetToNowDisabled.value) return;
	startDate.value = props.adapter.now();
}

function setEndToNow() {
	if (isEndSetToNowDisabled.value) return;
	endDate.value = props.adapter.now();
}

function clearRange() {
	if (!props.clearable) return;
	clearStartDate();
	clearEndDate();
}

function clearStartDate() {
	startDate.value = null;
	startPlaceholderRef.value = todayCalendarDate.value;
}

function clearEndDate() {
	endDate.value = null;
	endPlaceholderRef.value = todayCalendarDate.value;
}

function isFutureDate(date: DateValue) {
	return date.compare(todayCalendarDate.value) > 0;
}

function isStartDateUnavailable(date: DateValue) {
	return (props.disableFutureDates && isFutureDate(date)) || (calendarEndDate.value != null && date.compare(calendarEndDate.value) > 0);
}

function isEndDateUnavailable(date: DateValue) {
	return (props.disableFutureDates && isFutureDate(date)) || (calendarStartDate.value != null && date.compare(calendarStartDate.value) < 0);
}

const rangePopoverOpen = ref(false);

function focusFirstInput() {
	if (props.disabled || props.readonly) return;

	rangePopoverOpen.value = true;
}

defineExpose({ focusFirstInput });

watch(rangePopoverOpen, (open) => {
	if (!open) return;
	startPlaceholderRef.value = calendarStartDate.value ?? todayCalendarDate.value;
	endPlaceholderRef.value = calendarEndDate.value ?? todayCalendarDate.value;
});
</script>

<template>
	<div class="min-w-0" :class="block ? 'w-full' : 'w-fit'">
		<DPopover
			v-model:open="rangePopoverOpen"
			:disabled
			:content="{ ...pickerPopoverContent, align: 'end' }"
			:ui="pickerPopoverUi"
		>
			<slot
				name="trigger"
				:range-field-display
				:is-range-empty
				:can-clear-range
				:clear-range
			/>

			<template #content>
				<div data-testid="date-range-popover" class="flex gap-4">
					<DateTimePickerRangePanel
						v-model:placeholder="startPlaceholder"
						panel="start"
						:readonly
						:selected-date="selectedStartDate"
						:display-range
						:allow-non-contiguous-ranges
						:is-date-unavailable="isStartDateUnavailable"
						:is-set-to-now-disabled="isStartSetToNowDisabled"
						:show-clear="clearable"
						:size
						:disabled
						@clear="clearStartDate"
						@set-to-now="setStartToNow"
						@update:calendar="onStartCalendarUpdate"
					>
						<template #extra>
							<slot name="start-extra" />
						</template>
					</DateTimePickerRangePanel>
					<DateTimePickerRangePanel
						v-model:placeholder="endPlaceholder"
						panel="end"
						:readonly
						:selected-date="selectedEndDate"
						:display-range
						:allow-non-contiguous-ranges
						:is-date-unavailable="isEndDateUnavailable"
						:is-set-to-now-disabled="isEndSetToNowDisabled"
						:show-clear="clearable"
						:size
						:disabled
						@clear="clearEndDate"
						@set-to-now="setEndToNow"
						@update:calendar="onEndCalendarUpdate"
					>
						<template #extra>
							<slot name="end-extra" />
						</template>
					</DateTimePickerRangePanel>
				</div>
			</template>
		</DPopover>
	</div>
</template>
